'use client';

console.warn('[CommandCenterContext] Module loaded at', new Date().toISOString());

/**
 * Command Center Context Provider
 * Manages entities, assessments, and provides workspace-scoped functionality
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import type {
  Entity,
  EntityType,
  EntityStatus,
  EntityLink,
  Assessment,
  MatchSuggestion,
  CreateEntityParams,
  UpdateEntityParams,
  CreateEntityLinkParams,
  EntityFilters,
  AssessmentFilters,
  DashboardData,
  PaginatedResponse,
} from './types';

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface CommandCenterStats {
  total_entities: number;
  active_entities: number;
  total_links: number;
  total_mappings: number;
  total_assessments: number;
  pending_matches: number;
  entities_by_type: { entity_type: EntityType; count: number }[];
}

interface CommandCenterContextValue {
  // State
  workspaceId: string | null;
  entities: Entity[];
  assessments: Assessment[];
  matchSuggestions: MatchSuggestion[];
  stats: CommandCenterStats | null;
  loading: boolean;
  error: string | null;

  // Entity operations
  loadEntities: (filters?: EntityFilters, page?: number, pageSize?: number) => Promise<PaginatedResponse<Entity>>;
  getEntity: (id: string) => Promise<Entity | null>;
  createEntity: (params: Omit<CreateEntityParams, 'workspace_id'>) => Promise<Entity>;
  updateEntity: (id: string, params: UpdateEntityParams) => Promise<Entity>;
  deleteEntity: (id: string) => Promise<void>;

  // Entity link operations
  createEntityLink: (params: Omit<CreateEntityLinkParams, 'workspace_id'>) => Promise<EntityLink>;
  deleteEntityLink: (entityId: string, linkId: string) => Promise<void>;

  // Assessment operations
  loadAssessments: (filters?: AssessmentFilters, page?: number, pageSize?: number) => Promise<PaginatedResponse<Assessment>>;
  getAssessment: (id: string) => Promise<Assessment | null>;

  // Match operations
  loadPendingMatches: () => Promise<void>;
  acceptMatch: (suggestionId: string) => Promise<void>;
  rejectMatch: (suggestionId: string) => Promise<void>;

  // Refresh
  refreshStats: () => Promise<void>;
  refreshAll: () => Promise<void>;

  // Workspace management
  setWorkspaceId: (id: string) => void;
}

const CommandCenterContext = createContext<CommandCenterContextValue | undefined>(undefined);

// ============================================================================
// API HELPERS
// ============================================================================

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`/api/secure/command-center${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface CommandCenterProviderProps {
  children: ReactNode;
  initialWorkspaceId?: string;
}

export function CommandCenterProvider({
  children,
  initialWorkspaceId,
}: CommandCenterProviderProps) {
  const [workspaceId, setWorkspaceId] = useState<string | null>(initialWorkspaceId || null);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [matchSuggestions, setMatchSuggestions] = useState<MatchSuggestion[]>([]);
  const [stats, setStats] = useState<CommandCenterStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ============================================================================
  // ENTITY OPERATIONS
  // ============================================================================

  const loadEntities = useCallback(async (
    filters?: EntityFilters,
    page = 1,
    pageSize = 50
  ): Promise<PaginatedResponse<Entity>> => {
    if (!workspaceId) {
      return { data: [], total: 0, page: 1, pageSize: 50, hasMore: false };
    }

    const params = new URLSearchParams({
      workspace_id: workspaceId,
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (filters?.entity_type) {
      params.set('entity_type', Array.isArray(filters.entity_type)
        ? filters.entity_type.join(',')
        : filters.entity_type
      );
    }
    if (filters?.status) params.set('status', filters.status);
    if (filters?.tags?.length) params.set('tags', filters.tags.join(','));
    if (filters?.search) params.set('search', filters.search);
    if (filters?.external_source) params.set('external_source', filters.external_source);

    const result = await fetchApi<PaginatedResponse<Entity>>(`/entities?${params}`);
    setEntities(result.data);
    return result;
  }, [workspaceId]);

  const getEntity = useCallback(async (id: string): Promise<Entity | null> => {
    try {
      return await fetchApi<Entity>(`/entities/${id}`);
    } catch {
      return null;
    }
  }, []);

  const createEntity = useCallback(async (
    params: Omit<CreateEntityParams, 'workspace_id'>
  ): Promise<Entity> => {
    if (!workspaceId) {
      throw new Error('No workspace selected');
    }

    const entity = await fetchApi<Entity>('/entities', {
      method: 'POST',
      body: JSON.stringify({ ...params, workspace_id: workspaceId }),
    });

    setEntities(prev => [entity, ...prev]);
    await refreshStats();
    return entity;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const updateEntity = useCallback(async (
    id: string,
    params: UpdateEntityParams
  ): Promise<Entity> => {
    const updated = await fetchApi<Entity>(`/entities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(params),
    });

    setEntities(prev => prev.map(e => e.id === id ? updated : e));
    return updated;
  }, []);

  const deleteEntity = useCallback(async (id: string): Promise<void> => {
    await fetchApi(`/entities/${id}`, { method: 'DELETE' });
    setEntities(prev => prev.filter(e => e.id !== id));
    await refreshStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================================
  // ENTITY LINK OPERATIONS
  // ============================================================================

  const createEntityLink = useCallback(async (
    params: Omit<CreateEntityLinkParams, 'workspace_id'>
  ): Promise<EntityLink> => {
    if (!workspaceId) {
      throw new Error('No workspace selected');
    }

    return await fetchApi<EntityLink>(`/entities/${params.source_entity_id}/links`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }, [workspaceId]);

  const deleteEntityLink = useCallback(async (
    entityId: string,
    linkId: string
  ): Promise<void> => {
    await fetchApi(`/entities/${entityId}/links?linkId=${linkId}`, {
      method: 'DELETE',
    });
  }, []);

  // ============================================================================
  // ASSESSMENT OPERATIONS
  // ============================================================================

  const loadAssessments = useCallback(async (
    filters?: AssessmentFilters,
    page = 1,
    pageSize = 50
  ): Promise<PaginatedResponse<Assessment>> => {
    if (!workspaceId) {
      return { data: [], total: 0, page: 1, pageSize: 50, hasMore: false };
    }

    const params = new URLSearchParams({
      workspace_id: workspaceId,
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (filters?.tool_id) params.set('tool_id', filters.tool_id);
    if (filters?.tool_category) {
      params.set('tool_category', Array.isArray(filters.tool_category)
        ? filters.tool_category.join(',')
        : filters.tool_category
      );
    }
    if (filters?.status) params.set('status', filters.status);

    const result = await fetchApi<PaginatedResponse<Assessment>>(`/assessments?${params}`);
    setAssessments(result.data);
    return result;
  }, [workspaceId]);

  const getAssessment = useCallback(async (id: string): Promise<Assessment | null> => {
    try {
      return await fetchApi<Assessment>(`/assessments/${id}`);
    } catch {
      return null;
    }
  }, []);

  // ============================================================================
  // MATCH OPERATIONS
  // ============================================================================

  const loadPendingMatches = useCallback(async (): Promise<void> => {
    if (!workspaceId) return;

    const result = await fetchApi<{ suggestions: MatchSuggestion[]; total: number }>(
      `/match-suggestions?workspace_id=${workspaceId}&status=pending`
    );
    setMatchSuggestions(result.suggestions || []);
  }, [workspaceId]);

  const acceptMatch = useCallback(async (suggestionId: string): Promise<void> => {
    await fetchApi(`/match-suggestions/${suggestionId}/accept`, { method: 'POST' });
    setMatchSuggestions(prev => prev.filter(m => m.id !== suggestionId));
    await refreshStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rejectMatch = useCallback(async (suggestionId: string): Promise<void> => {
    await fetchApi(`/match-suggestions/${suggestionId}/reject`, { method: 'POST' });
    setMatchSuggestions(prev => prev.filter(m => m.id !== suggestionId));
  }, []);

  // ============================================================================
  // REFRESH OPERATIONS
  // ============================================================================

  const refreshStats = useCallback(async (): Promise<void> => {
    if (!workspaceId) return;

    try {
      const result = await fetchApi<{
        stats: {
          total_entities: number;
          active_entities: number;
          total_links: number;
          total_mappings: number;
          total_assessments: number;
          pending_matches: number;
        };
        entities_by_type: { entity_type: EntityType; count: number }[];
        pending_matches: MatchSuggestion[];
      }>(`/dashboard?workspace_id=${workspaceId}`);

      setStats({
        ...result.stats,
        entities_by_type: result.entities_by_type || [],
      });

      // Also update pending matches from dashboard
      if (result.pending_matches) {
        setMatchSuggestions(result.pending_matches);
      }
    } catch (err) {
      console.error('Failed to refresh stats:', err);
    }
  }, [workspaceId]);

  const refreshAll = useCallback(async (): Promise<void> => {
    console.warn('[CommandCenter] refreshAll called, workspaceId:', workspaceId);
    if (!workspaceId) {
      console.warn('[CommandCenter] refreshAll: no workspaceId, setting loading=false');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    console.warn('[CommandCenter] refreshAll: loading=true, starting API calls');

    try {
      await Promise.all([
        loadEntities().then(r => { console.warn('[CommandCenter] loadEntities done'); return r; }),
        loadAssessments().then(r => { console.warn('[CommandCenter] loadAssessments done'); return r; }),
        loadPendingMatches().then(() => { console.warn('[CommandCenter] loadPendingMatches done'); }),
        refreshStats().then(() => { console.warn('[CommandCenter] refreshStats done'); }),
      ]);
      console.warn('[CommandCenter] refreshAll: all API calls completed');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Failed to refresh Command Center data:', err);
    } finally {
      console.warn('[CommandCenter] refreshAll: finally block, setting loading=false');
      setLoading(false);
    }
  }, [workspaceId, loadEntities, loadAssessments, loadPendingMatches, refreshStats]);

  // Load initial data when workspace changes
  useEffect(() => {
    if (workspaceId) {
      console.warn('[CommandCenter] Workspace set, calling refreshAll:', workspaceId);
      refreshAll();
    } else {
      console.warn('[CommandCenter] No workspace, setting loading=false');
      setLoading(false);
    }
  }, [workspaceId, refreshAll]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value: CommandCenterContextValue = {
    workspaceId,
    entities,
    assessments,
    matchSuggestions,
    stats,
    loading,
    error,
    loadEntities,
    getEntity,
    createEntity,
    updateEntity,
    deleteEntity,
    createEntityLink,
    deleteEntityLink,
    loadAssessments,
    getAssessment,
    loadPendingMatches,
    acceptMatch,
    rejectMatch,
    refreshStats,
    refreshAll,
    setWorkspaceId,
  };

  return (
    <CommandCenterContext.Provider value={value}>
      {children}
    </CommandCenterContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

export function useCommandCenter() {
  const context = useContext(CommandCenterContext);
  if (!context) {
    throw new Error('useCommandCenter must be used within CommandCenterProvider');
  }
  return context;
}

/**
 * Hook for checking if Command Center is available and has a workspace
 */
export function useCommandCenterReady() {
  const context = useContext(CommandCenterContext);
  return {
    available: context !== undefined,
    ready: context !== undefined && context.workspaceId !== null && !context.loading,
    loading: context?.loading ?? true,
    workspaceId: context?.workspaceId ?? null,
  };
}
